import type { MessageResponse } from '@voll-intelligence/domain';

export interface ThreadMessage {
  id: string;
  author: MessageResponse['author'];
  text: string;
  at: Date;
  ui?: unknown;
  translation?: { text: string; engine: 'amazon-translate' | 'operator-original' };
}

export function messageToThread(m: MessageResponse): ThreadMessage {
  // Backend `author` is the canonical platform vocabulary
  // (`customer | agent | operator | system`) — pass it through directly.
  const author: ThreadMessage['author'] = m.author;

  let text: string;
  // Opaque generative-UI block (ChatUi[]) the read API carries on agent text
  // replies (`payload.ui`). Kept opaque here — validated at the render boundary by
  // genui-kit's `ChatUiMessageSchema`, mirroring web-chat. Only text replies carry it.
  let ui: unknown;
  if (m.messageType === 'text') {
    const p = m.payload;
    text = p.body;
    ui = p.ui;
  } else {
    // audio-transcription: the read API projects the transcript onto `payload.body`
    // (+ a `transcribed` flag) for audio, so prefer it. caption/url stay the
    // fallback for media without a transcript (image/video/document, or fail-open
    // audio where the provider was unavailable).
    const p = m.payload as { body?: string; caption?: string; url?: string };
    text = p.body ?? p.caption ?? p.url ?? `[${m.messageType}]`;
  }

  return {
    id: m.messageId,
    author,
    text,
    at: m.timestamp,
    ...(ui === undefined ? {} : { ui }),
    ...(m.translation
      ? { translation: { text: m.translation.text, engine: m.translation.engine } }
      : {}),
  };
}

export function messagesToThread(ms: MessageResponse[]): ThreadMessage[] {
  return ms.map(messageToThread);
}

export function dedupById(ms: ThreadMessage[]): ThreadMessage[] {
  const byId = new Map<string, ThreadMessage>();
  for (const m of ms) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) => a.at.getTime() - b.at.getTime());
}
